// apps/web/src/routes/(showcase)/showcase/booking.tsx

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@acme/ui/alert-dialog";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BookingDetailsDialog } from "~/components/showcase/calendar/booking-details-dialog";
import { BookingPanel } from "~/components/showcase/calendar/booking-panel";
import {
  type CalendarMode,
  CalendarModeToggle,
} from "~/components/showcase/calendar/calendar-mode-toggle";
import { GuestMonthGrid } from "~/components/showcase/calendar/guest-month-grid";
import {
  type BookingChannel,
  OperatorTimeline,
} from "~/components/showcase/calendar/operator-timeline";
import {
  RoomPicker,
  type RoomPickerRoom,
} from "~/components/showcase/calendar/room-picker";
import { ViewToggle } from "~/components/showcase/calendar/view-toggle";
import { ShowcaseToast } from "~/components/showcase/toast";
import { seo } from "~/lib/seo";
import {
  cancelBookingFn,
  listBookingsFn,
  updateBookingFn,
} from "~/lib/showcase/api-client";
import { nightlyPriceFor } from "~/lib/showcase/pricing";
import { useShowcaseEvents } from "~/lib/showcase/use-realtime";
import {
  generateStructuredDataGraph,
  getShowcasePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";
import { getLocale } from "~/paraglide/runtime.js";

export const Route = createFileRoute("/(showcase)/showcase/booking")({
  component: BookingDemo,
  head: () => {
    const title = m.meta_showcase_booking_title();
    const description = m.meta_showcase_booking_description();
    const seoData = seo({
      // The hospitality booking demo no longer matches what Lindevo sells.
      // Kept working and crawlable, but out of the index until it is ported
      // out to apps/mockup. Also removed from sitemap.xml.
      noindex: true,
      description,
      path: "/showcase/booking",
      title,
    });
    const structuredData = generateStructuredDataGraph(
      getShowcasePageSchemas({
        description,
        pageName: title,
        path: "/showcase/booking",
      })
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [{ children: structuredData, type: "application/ld+json" }],
    };
  },
  loader: async () => ({ initialBookings: await listBookingsFn() }),
});

interface ServerBooking {
  checkIn: string;
  checkOut: string;
  guestName: string;
  guests: number;
  id: string;
  roomId: string;
  totalPrice: string;
}

interface DialogRoom {
  id: string;
  nameEn: string;
  nameLv: string;
}

function resolveSelectedBooking(
  bookings: ServerBooking[],
  rooms: DialogRoom[],
  selectedBookingId: string | null,
  locale: string
) {
  if (!selectedBookingId) {
    return { booking: null, roomLabel: "" };
  }
  const raw = bookings.find((b) => b.id === selectedBookingId);
  if (!raw) {
    return { booking: null, roomLabel: "" };
  }
  const room = rooms.find((r) => r.id === raw.roomId);
  let roomLabel = "";
  if (room) {
    roomLabel = locale === "lv" ? room.nameLv : room.nameEn;
  }
  return {
    booking: {
      channel: ((raw as { channel?: string }).channel ??
        "direct") as BookingChannel,
      checkIn: raw.checkIn,
      checkOut: raw.checkOut,
      guestName: raw.guestName,
      guests: raw.guests,
      id: raw.id,
      roomId: raw.roomId,
      totalPrice: raw.totalPrice,
    },
    roomLabel,
  };
}

function expandBookingDates(checkIn: string, checkOut: string): string[] {
  const out: string[] = [];
  const cursor = new Date(checkIn);
  const end = new Date(checkOut);
  while (cursor < end) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

function computeFullyBlockedDates(
  bookings: ServerBooking[],
  totalRooms: number
): Set<string> {
  const blocked = new Set<string>();
  if (totalRooms === 0) {
    return blocked;
  }
  const roomsBookedByDate = new Map<string, Set<string>>();
  for (const b of bookings) {
    for (const day of expandBookingDates(b.checkIn, b.checkOut)) {
      let roomSet = roomsBookedByDate.get(day);
      if (!roomSet) {
        roomSet = new Set<string>();
        roomsBookedByDate.set(day, roomSet);
      }
      roomSet.add(b.roomId);
    }
  }
  for (const [day, roomIds] of roomsBookedByDate) {
    if (roomIds.size >= totalRooms) {
      blocked.add(day);
    }
  }
  return blocked;
}

function computeBlockedDatesForRoom(
  bookings: ServerBooking[],
  roomId: string
): Set<string> {
  const set = new Set<string>();
  for (const b of bookings) {
    if (b.roomId !== roomId) {
      continue;
    }
    for (const day of expandBookingDates(b.checkIn, b.checkOut)) {
      set.add(day);
    }
  }
  return set;
}

function parseGuestsParam(search: unknown): number {
  const raw = (search as Record<string, unknown>).guests;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 1 && raw <= 6) {
    return raw;
  }
  if (typeof raw === "string") {
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1 && n <= 6) {
      return n;
    }
  }
  return 2;
}

function BookingDemo() {
  const { initialBookings } = Route.useLoaderData();
  const locale = getLocale();
  const [view, setView] = useState<"guest" | "operator">("guest");
  const [calendarMode, setCalendarMode] = useState<CalendarMode>("dates_first");
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [bookings, setBookings] = useState(initialBookings);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [preferredRoomId, setPreferredRoomId] = useState<string | null>(null);
  const location = useLocation();
  const [guests] = useState(() => parseGuestsParam(location.search));
  const [toast, setToast] = useState<string | null>(null);
  const [rooms, setRooms] = useState<RoomPickerRoom[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const fn = () => setIsMobile(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    fetch("/api/showcase/rooms")
      .then((r) => r.json())
      .then(setRooms)
      .catch(() => setRooms([]));
  }, []);

  const refreshBookings = () => listBookingsFn().then(setBookings);

  useShowcaseEvents((e) => {
    if (
      e.type === "booking_created" ||
      e.type === "booking_cancelled" ||
      e.type === "booking_updated" ||
      e.type === "session_reset"
    ) {
      refreshBookings();
    }
  });

  // A date is only fully blocked when every room has a booking covering it.
  // With multiple rooms in inventory, blocking on a single overlap would hide
  // most of the calendar even though plenty of other rooms are still free.
  const blockedDates = computeFullyBlockedDates(bookings, rooms.length);

  const selectedRoom = selectedRoomId
    ? rooms.find((r) => r.id === selectedRoomId)
    : null;

  const priceForDateOnRoom = selectedRoom
    ? (isoDate: string) => {
        const quote = nightlyPriceFor(
          selectedRoom.baseRate,
          selectedRoom.id,
          isoDate
        );
        return `€${quote.amount}`;
      }
    : undefined;

  const closeBookingPanel = () => {
    setRange(null);
    setPreferredRoomId(null);
  };

  const handleModeChange = (next: CalendarMode) => {
    setCalendarMode(next);
    setSelectedRoomId(null);
  };

  const handleRoomFirstSelect = (next: { from: string; to: string }) => {
    setRange(next);
    if (selectedRoomId) {
      setPreferredRoomId(selectedRoomId);
    }
  };

  const { booking: selectedBooking, roomLabel: selectedRoomLabel } =
    resolveSelectedBooking(bookings, rooms, selectedBookingId, locale);

  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="font-[family-name:var(--sc-font-display)] text-2xl">
          {m.showcase_calendar_heading()}
        </h1>
        <div className="flex items-center gap-2">
          <ViewToggle onChange={setView} value={view} />
        </div>
      </div>

      {view === "guest" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CalendarModeToggle
              onChange={handleModeChange}
              value={calendarMode}
            />
            {calendarMode === "room_first" && selectedRoom && (
              <div className="flex items-center gap-3 text-[var(--sc-muted)] text-xs">
                <span>
                  {m.showcase_calendar_room_availability_caption({
                    room:
                      locale === "lv"
                        ? selectedRoom.nameLv
                        : selectedRoom.nameEn,
                  })}
                </span>
                <button
                  className="underline transition hover:text-[var(--sc-fg)]"
                  onClick={() => setSelectedRoomId(null)}
                  type="button"
                >
                  {m.showcase_calendar_room_picker_change_room()}
                </button>
              </div>
            )}
          </div>

          {calendarMode === "dates_first" && (
            <GuestMonthGrid
              blockedDates={blockedDates}
              onSelect={setRange}
              selected={range}
            />
          )}

          {calendarMode === "room_first" && !selectedRoom && (
            <RoomPicker onPick={setSelectedRoomId} rooms={rooms} />
          )}

          {calendarMode === "room_first" && selectedRoom && (
            <GuestMonthGrid
              blockedDates={computeBlockedDatesForRoom(
                bookings,
                selectedRoom.id
              )}
              onSelect={handleRoomFirstSelect}
              priceForDate={priceForDateOnRoom}
              selected={range}
            />
          )}
        </div>
      )}

      {view === "operator" && isMobile && (
        <div className="rounded-[var(--sc-radius)] border border-[var(--sc-border)] bg-[var(--sc-surface)] p-6 text-center">
          <p className="mb-3 text-[var(--sc-muted)]">
            {m.showcase_calendar_mobile_op_warning()}
          </p>
          <button
            className="rounded-[var(--sc-radius)] bg-[var(--sc-fg)] px-4 py-2 text-[var(--sc-bg)]"
            onClick={() => setView("guest")}
            type="button"
          >
            {m.showcase_calendar_switch_to_guest()}
          </button>
        </div>
      )}

      {view === "operator" && !isMobile && (
        <OperatorTimeline
          bookings={bookings.map((b) => ({
            channel: ((b as { channel?: string }).channel ??
              "direct") as BookingChannel,
            checkIn: b.checkIn,
            checkOut: b.checkOut,
            guestName: b.guestName,
            id: b.id,
            roomId: b.roomId,
          }))}
          onBookingClick={(id) => {
            setSelectedBookingId(id);
          }}
          onRangeSelect={({ from, to, roomId }) => {
            setRange({ from, to });
            setPreferredRoomId(roomId);
          }}
          rooms={rooms}
        />
      )}

      {range ? (
        <BookingPanel
          guests={guests}
          onBooked={() => {
            // Confirmation is shown inside the drawer (see BookingPanel).
            // Refresh blocked-dates / availability so the just-booked range
            // is reflected in the calendar without leaving the page.
            refreshBookings();
          }}
          onClose={closeBookingPanel}
          preferredRoomId={preferredRoomId}
          range={range}
        />
      ) : null}

      <BookingDetailsDialog
        booking={selectedBooking}
        editable={view === "operator"}
        onClose={() => setSelectedBookingId(null)}
        onRequestCancel={(id) => {
          setSelectedBookingId(null);
          setPendingCancelId(id);
        }}
        onSubmitEdit={async (input) => {
          try {
            const result = await updateBookingFn({ data: input });
            if (result.ok) {
              await refreshBookings();
              setToast(m.showcase_calendar_booking_updated_toast());
              return { ok: true } as const;
            }
            return {
              code: result.code as
                | "invalid_dates"
                | "overlap"
                | "capacity"
                | "invalid_channel"
                | "not_found"
                | "generic",
              ok: false,
            } as const;
          } catch {
            return { code: "generic", ok: false } as const;
          }
        }}
        roomLabel={selectedRoomLabel}
      />

      <AlertDialog
        onOpenChange={(open) => !open && setPendingCancelId(null)}
        open={pendingCancelId !== null}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {m.showcase_calendar_cancel_title()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {m.showcase_calendar_cancel_body()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {m.showcase_calendar_cancel_keep()}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingCancelId) {
                  return;
                }
                await cancelBookingFn({
                  data: { bookingId: pendingCancelId },
                });
                setPendingCancelId(null);
                setToast(m.showcase_calendar_cancelled_toast());
              }}
            >
              {m.showcase_calendar_cancel_confirm()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShowcaseToast message={toast} onDismiss={() => setToast(null)} />
    </section>
  );
}
